import { ArrowLeft, ArrowRight, Check, CheckCircle, ChevronDown, Clock, MessageCircle, X, XCircle } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useParams } from 'react-router-dom';
import { employeeAPI, timesheetAPI } from '../../utils/api';

const EmployeeTimesheets = () => {
  const { id } = useParams(); // employee id
  const [employee, setEmployee] = useState(null);
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('week');
  const [currentDate, setCurrentDate] = useState(new Date());

  // Modal state
  const [modalDay, setModalDay] = useState(null); // Date object
  const [modalEntries, setModalEntries] = useState([]);

  // Bulk approval state
  const [selectedTimesheets, setSelectedTimesheets] = useState(new Set());
  const [selectAllMode, setSelectAllMode] = useState(false);

  // Individual timesheet review modal
  const [reviewModal, setReviewModal] = useState(null);
  const [reviewAction, setReviewAction] = useState(null); // 'approve' or 'decline'
  const [adjustedHours, setAdjustedHours] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Maintain previous day reference to reopen after review (optional UX enhancement)
  const [previousModalDay, setPreviousModalDay] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [empRes, tsRes] = await Promise.all([
          employeeAPI.getEmployee(id),
          timesheetAPI.getTimesheets({ employee: id }),
        ]);
        setEmployee(empRes.data.data);
        setTimesheets(tsRes.data.data || []);
      } catch (err) {
        console.error('Failed to load timesheets', err);
        toast.error('Failed to load timesheets');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const refreshTimesheets = async () => {
    try {
      const tsRes = await timesheetAPI.getTimesheets({ employee: id });
      setTimesheets(tsRes.data.data || []);
      setSelectedTimesheets(new Set());
      setSelectAllMode(false);
    } catch (err) {
      console.error('Failed to refresh timesheets', err);
    }
  };

  const formatDate = (d) => {
    return new Date(d).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatLocalDate = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const getTimesheetsForDay = (day) => {
    return timesheets.filter((ts) => {
      const tsDate = new Date(ts.date);
      return (
        tsDate.getFullYear() === day.getFullYear() &&
        tsDate.getMonth() === day.getMonth() &&
        tsDate.getDate() === day.getDate()
      );
    });
  };

  const getHoursForDay = (day) => {
    return getTimesheetsForDay(day).reduce((sum, t) => sum + t.hours, 0);
  };

  const getPendingTimesheets = () => {
    return timesheets.filter(ts => ts.status === 'Pending');
  };

  const generateWeekDays = () => {
    const start = new Date(currentDate);
    start.setDate(start.getDate() - start.getDay());
    return Array.from({ length: 7 }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  };

  const generateMonthDays = () => {
    const days = [];
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const start = new Date(first);
    start.setDate(first.getDate() - first.getDay());
    const end = new Date(last);
    end.setDate(last.getDate() + (6 - last.getDay()));
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      days.push(new Date(d));
    }
    return days;
  };

  const openModalForDay = (day) => {
    setPreviousModalDay(day);
    setModalDay(day);
    setModalEntries(getTimesheetsForDay(day));
  };

  // Close whichever modal is open, restoring the day modal only when invoked programmatically
  const closeModal = () => {
    setReviewModal(null);
    setReviewAction(null);
    setAdjustedHours('');
    setRejectionReason('');

    if (previousModalDay) {
      // Coming from programmatic close after approve/decline → reopen the day modal
      setModalDay(previousModalDay);
      setPreviousModalDay(null);
    } else {
      setModalDay(null);
    }
  };

  // Force-close all modals (used by the "X" buttons so one click suffices)
  const handleCloseAll = () => {
    setReviewModal(null);
    setReviewAction(null);
    setAdjustedHours('');
    setRejectionReason('');
    setPreviousModalDay(null);
    setModalDay(null);
  };

  const openReviewModal = (timesheet, e) => {
    e.stopPropagation();
    setPreviousModalDay(modalDay);
    setModalDay(null); // hide day modal
    setReviewModal(timesheet);
    setAdjustedHours(timesheet.hours.toString());
  };

  // Bulk selection functions
  const toggleTimesheetSelection = (timesheetId) => {
    const newSelected = new Set(selectedTimesheets);
    if (newSelected.has(timesheetId)) {
      newSelected.delete(timesheetId);
    } else {
      newSelected.add(timesheetId);
    }
    setSelectedTimesheets(newSelected);
  };

  const toggleSelectAll = () => {
    const pendingTimesheets = getPendingTimesheets();
    if (selectAllMode || selectedTimesheets.size === pendingTimesheets.length) {
      // Deselect all
      setSelectedTimesheets(new Set());
      setSelectAllMode(false);
    } else {
      // Select all pending timesheets
      const allPendingIds = new Set(pendingTimesheets.map(ts => ts._id));
      setSelectedTimesheets(allPendingIds);
      setSelectAllMode(true);
    }
  };

  // Approval functions
  const handleBulkApproval = async () => {
    if (selectedTimesheets.size === 0) {
      toast.error('No timesheets selected');
      return;
    }

    try {
      setSubmitting(true);
      
      // Use the bulk approve endpoint for better performance
      await timesheetAPI.bulkApproveTimesheets(Array.from(selectedTimesheets), {
        approved: true,
        status: 'Approved'
      });
      
      toast.success(`${selectedTimesheets.size} timesheet(s) approved successfully`);
      await refreshTimesheets();
    } catch (error) {
      console.error('Bulk approval failed:', error);
      toast.error(error.response?.data?.message || 'Failed to approve some timesheets');
    } finally {
      setSubmitting(false);
    }
  };

  const handleIndividualAction = async () => {
    if (!reviewModal || !reviewAction) return;

    try {
      setSubmitting(true);
      
      if (reviewAction === 'approve') {
        await timesheetAPI.approveTimesheet(reviewModal._id, { 
          approved: true, 
          status: 'Approved',
          adjustedHours: parseFloat(adjustedHours) !== reviewModal.hours ? parseFloat(adjustedHours) : undefined
        });
        toast.success('Timesheet approved successfully');
      } else if (reviewAction === 'decline') {
        const hoursToDecline = parseFloat(adjustedHours) || 0;
        await timesheetAPI.approveTimesheet(reviewModal._id, { 
          approved: false, 
          status: 'Rejected',
          rejectedHours: hoursToDecline,
          rejectionReason: rejectionReason.trim() || 'Hours adjusted by employer'
        });
        toast.success('Timesheet declined successfully');
      }
      
      await refreshTimesheets();
      closeModal();
    } catch (error) {
      console.error('Action failed:', error);
      toast.error('Failed to process timesheet');
    } finally {
      setSubmitting(false);
    }
  };

  const renderDayCard = (day, isToday, inCurrentMonth = true) => {
    const tsDay = getTimesheetsForDay(day);
    const total = getHoursForDay(day);
    const hasPending = tsDay.some(ts => ts.status === 'Pending');
    const pendingTimesheets = tsDay.filter(ts => ts.status === 'Pending');
    const hasSelectedTimesheets = pendingTimesheets.some(ts => selectedTimesheets.has(ts._id));

    // Color based on hours and status
    let borderColor = 'border-gray-100';
    let headerBg = 'bg-gray-50';
    
    if (hasPending) {
      borderColor = 'border-yellow-300';
      headerBg = 'bg-yellow-50';
    } else if (total === 0) {
      borderColor = 'border-gray-300';
      headerBg = 'bg-gray-50';
    } else if (total >= 8) {
      borderColor = 'border-green-300';
      headerBg = 'bg-green-50';
    } else {
      borderColor = 'border-blue-300';
      headerBg = 'bg-blue-50';
    }

    return (
      <div
        key={formatLocalDate(day)}
        onClick={() => openModalForDay(day)}
        className={`cursor-pointer bg-white rounded-xl shadow-soft border ${isToday ? 'border-primary-300' : borderColor} overflow-hidden ${!inCurrentMonth ? 'opacity-50' : ''} hover:shadow-elegant transition-all duration-200 relative`}
      >
        {/* Bulk Selection Checkbox */}
        {hasPending && (selectAllMode || selectedTimesheets.size > 0) && (
          <div className="absolute top-2 right-2 z-10">
            <input
              type="checkbox"
              checked={pendingTimesheets.every(ts => selectedTimesheets.has(ts._id))}
              onChange={(e) => {
                e.stopPropagation();
                pendingTimesheets.forEach(ts => {
                  if (e.target.checked) {
                    setSelectedTimesheets(prev => new Set([...prev, ts._id]));
                  } else {
                    setSelectedTimesheets(prev => {
                      const newSet = new Set(prev);
                      newSet.delete(ts._id);
                      return newSet;
                    });
                  }
                });
              }}
              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 bg-white"
            />
          </div>
        )}

        <div className={`p-3 text-center border-b ${isToday ? 'bg-primary-50 border-primary-100' : headerBg + ' border-gray-100'}`}>
          <p className="text-xs text-gray-500">{day.toLocaleDateString('en-US', { weekday: 'short' })}</p>
          <p className={`text-lg font-medium ${isToday ? 'text-primary-700' : 'text-gray-900'}`}>{day.getDate()}</p>
          {hasPending && (
            <div className="w-2 h-2 bg-yellow-400 rounded-full mx-auto mt-1"></div>
          )}
          {hasSelectedTimesheets && (
            <div className="w-2 h-2 bg-primary-500 rounded-full mx-auto mt-1"></div>
          )}
        </div>
        <div className="p-3">
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs text-gray-500">Total Hours</span>
            <span className="text-sm font-medium">{total}</span>
          </div>
          {tsDay.length > 0 ? (
            <div className="space-y-2">
              {tsDay.map((ts) => (
                <div key={ts._id} className="p-2 bg-gray-50 rounded-md border border-gray-100 text-xs">
                  <div className="font-medium text-gray-900 truncate">{ts.project}</div>
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-gray-500">{ts.hours} hours</span>
                    <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${
                      ts.status === 'Approved'
                        ? 'bg-green-100 text-green-700'
                        : ts.status === 'Rejected'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {ts.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-xs text-gray-400">No entries</p>
          )}
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    const days = generateWeekDays();
    const today = new Date();
    return <div className="grid grid-cols-7 gap-4">{days.map((d) => renderDayCard(d, d.toDateString() === today.toDateString()))}</div>;
  };

  const renderMonthView = () => {
    const days = generateMonthDays();
    const today = new Date();
    const currentMonth = currentDate.getMonth();
    return <div className="grid grid-cols-7 gap-4">{days.map((d) => renderDayCard(d, d.toDateString() === today.toDateString(), d.getMonth() === currentMonth))}</div>;
  };

  const pendingCount = getPendingTimesheets().length;

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3">
        <Link to="/employees" className="text-primary-600 hover:text-primary-700">Employees</Link>
        <span className="text-gray-400">/</span>
        <span className="font-medium">Timesheets</span>
      </div>
      
      <div className="flex justify-between items-start">
        <div>
          <h1 className="page-title">{employee ? `${employee.firstName} ${employee.lastName}` : 'Employee'} Timesheets</h1>
          {pendingCount > 0 && (
            <p className="text-yellow-600 mt-2 flex items-center">
              <Clock size={16} className="mr-1" />
              {pendingCount} timesheet(s) pending approval
            </p>
          )}
        </div>
        
        {/* Bulk Approval Controls */}
        {pendingCount > 0 && (
          <div className="flex items-center space-x-3">
            <button
              onClick={toggleSelectAll}
              className="btn-secondary text-sm"
            >
              {selectAllMode || selectedTimesheets.size === getPendingTimesheets().length ? 'Deselect All' : 'Select All Pending'}
            </button>
            {selectedTimesheets.size > 0 && (
              <button
                onClick={handleBulkApproval}
                disabled={submitting}
                className="btn-success text-sm flex items-center"
              >
                {submitting ? (
                  <>
                    <div className="loading-spinner mr-2" />
                    Approving...
                  </>
                ) : (
                  <>
                    <Check size={16} className="mr-2" />
                    Approve Selected ({selectedTimesheets.size})
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex justify-between items-center bg-white rounded-xl shadow-soft p-4 border border-gray-100">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => {
              setCurrentDate((prev) => {
                const newDate = new Date(prev);
                if (view === 'week') {
                  newDate.setDate(newDate.getDate() - 7);
                } else {
                  // Move to the first of the current month to avoid overflow issues (e.g., Jan-31 → Mar-03)
                  newDate.setDate(1);
                  newDate.setMonth(newDate.getMonth() - 1);
                }
                return newDate;
              });
            }}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <ArrowLeft size={16} />
          </button>
          <span className="font-medium text-gray-900">
            {view === 'week'
              ? (() => {
                  const start = generateWeekDays()[0];
                  const end = generateWeekDays()[6];
                  return `${formatDate(start)} - ${formatDate(end)}`;
                })()
              : new Date(currentDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </span>
          <button
            onClick={() => {
              setCurrentDate((prev) => {
                const newDate = new Date(prev);
                if (view === 'week') {
                  newDate.setDate(newDate.getDate() + 7);
                } else {
                  newDate.setDate(1);
                  newDate.setMonth(newDate.getMonth() + 1);
                }
                return newDate;
              });
            }}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <ArrowRight size={16} />
          </button>
        </div>

        <div className="relative">
          <select value={view} onChange={(e) => setView(e.target.value)} className="select-field pl-3 pr-8 py-2.5 appearance-none bg-white">
            <option value="week">Week View</option>
            <option value="month">Month View</option>
          </select>
          <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="loading-spinner w-8 h-8 border-4"></div>
        </div>
      ) : timesheets.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border border-gray-100">
          <Clock size={32} className="mx-auto mb-4 text-gray-400" />
          <p className="text-gray-600">No timesheets found for this employee.</p>
        </div>
      ) : (
        <div>
          {view === 'week' && renderWeekView()}
          {view === 'month' && renderMonthView()}
        </div>
      )}

      {/* Day Modal */}
      {modalDay && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-xl shadow-elegant w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Timesheets – {formatDate(modalDay)}</h3>
              <button onClick={handleCloseAll} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-4">
              {modalEntries.length === 0 ? (
                <p className="text-gray-600 text-sm">No entries for this day.</p>
              ) : (
                modalEntries.map((ts) => (
                  <div key={ts._id} className="border border-gray-100 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-medium text-gray-900">{ts.project}</p>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium">{ts.hours} hrs</span>
                            {ts.status === 'Pending' && (
                              <input
                                type="checkbox"
                                checked={selectedTimesheets.has(ts._id)}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => toggleTimesheetSelection(ts._id)}
                                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                              />
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-gray-700 mb-3 whitespace-pre-line">{ts.description}</p>
                        <div className="flex items-center justify-between">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            ts.status === 'Approved'
                              ? 'bg-green-100 text-green-700'
                              : ts.status === 'Rejected'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {ts.status}
                          </span>
                          
                          {ts.status === 'Pending' && (
                            <button
                              onClick={(e) => openReviewModal(ts, e)}
                              className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center"
                            >
                              <MessageCircle size={14} className="mr-1" />
                              Review
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Individual Review Modal */}
      {reviewModal && (
        <div className="fixed inset-0 z-70 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-xl shadow-elegant w-full max-w-lg">
            <div className="flex justify-between items-center p-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Review Timesheet</h3>
              <button onClick={handleCloseAll} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Timesheet Details */}
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-gray-900">{reviewModal.project}</span>
                  <span className="text-sm text-gray-500">{formatDate(new Date(reviewModal.date))}</span>
                </div>
                <p className="text-sm text-gray-700 mb-2">{reviewModal.description}</p>
                <p className="text-sm font-medium">Original Hours: {reviewModal.hours}</p>
              </div>

              {/* Action Selection */}
              <div className="space-y-3">
                <p className="font-medium text-gray-900">Choose Action:</p>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setReviewAction('approve')}
                    className={`flex-1 p-3 rounded-lg border text-center transition-colors ${
                      reviewAction === 'approve'
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <CheckCircle size={20} className="mx-auto mb-1" />
                    <span className="text-sm font-medium">Approve</span>
                  </button>
                  <button
                    onClick={() => setReviewAction('decline')}
                    className={`flex-1 p-3 rounded-lg border text-center transition-colors ${
                      reviewAction === 'decline'
                        ? 'border-red-500 bg-red-50 text-red-700'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <XCircle size={20} className="mx-auto mb-1" />
                    <span className="text-sm font-medium">Decline</span>
                  </button>
                </div>
              </div>

              {/* Hours Adjustment */}
              {reviewAction && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {reviewAction === 'approve' ? 'Adjust Hours (optional)' : 'Hours to Decline'}
                    </label>
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      max={reviewModal.hours}
                      value={adjustedHours}
                      onChange={(e) => setAdjustedHours(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter hours"
                    />
                    {reviewAction === 'decline' && (
                      <p className="text-xs text-gray-500 mt-1">
                        Enter the number of hours to decline (0 = decline all hours)
                      </p>
                    )}
                  </div>

                  {reviewAction === 'decline' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Reason for Decline (optional)
                      </label>
                      <textarea
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Explain why you're declining these hours..."
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
                <button
                  onClick={handleCloseAll}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleIndividualAction}
                  disabled={!reviewAction || submitting}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    reviewAction === 'approve'
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-red-600 text-white hover:bg-red-700'
                  }`}
                >
                  {submitting ? (
                    <>
                      <div className="loading-spinner mr-2" />
                      Processing...
                    </>
                  ) : (
                    `${reviewAction === 'approve' ? 'Approve' : 'Decline'} Timesheet`
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeTimesheets; 